// src/components/layout/PortalLayout.tsx
import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { usePortalConfig } from '@/hooks/queries';
import { ErrorBoundary } from '@/components/common';
import { SessionWarningDialog } from '@/components/common/SessionWarningDialog';
import { useSessionManager } from '@/hooks/useSessionManager';
import {
  LayoutDashboard,
  Ticket,
  MessageSquare,
  BookOpen,
  User,
  Menu,
  X,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, Button } from '@/components/ui';

interface PortalMenuItem {
  path: string;
  icon: LucideIcon;
  label: string;
  moduleKey?: 'tickets' | 'conversaciones' | 'conocimiento';
}

const portalMenuItems: PortalMenuItem[] = [
  { path: '/portal', icon: LayoutDashboard, label: 'Inicio' },
  { path: '/portal/tickets', icon: Ticket, label: 'Mis Tickets', moduleKey: 'tickets' },
  { path: '/portal/conversaciones', icon: MessageSquare, label: 'Mensajes', moduleKey: 'conversaciones' },
  { path: '/portal/conocimiento', icon: BookOpen, label: 'Ayuda', moduleKey: 'conocimiento' },
];

function PortalSidebar({
  isOpen,
  onClose,
  enabledModules,
}: {
  isOpen: boolean;
  onClose: () => void;
  enabledModules: { tickets: boolean; conversaciones: boolean; conocimiento: boolean };
}) {
  const filteredItems = portalMenuItems.filter(
    item => !item.moduleKey || enabledModules[item.moduleKey]
  );

  return (
    <>
      {/* Overlay para movil */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 border-r border-border bg-background transition-transform duration-200 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          <NavLink to="/portal" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <span className="text-lg font-bold text-primary-foreground">P</span>
            </div>
            <span className="text-xl font-bold text-foreground">
              Portal
            </span>
          </NavLink>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-accent lg:hidden"
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Navegacion */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {filteredItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/portal'}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )
              }
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Footer del sidebar */}
        <div className="border-t border-border p-3">
          <NavLink
            to="/portal/perfil"
            className={({ isActive }) =>
              cn(
                'flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            <User className="w-5 h-5 mr-3" />
            Mi Perfil
          </NavLink>
        </div>
      </aside>
    </>
  );
}

function PortalHeader({
  user,
  onMenuClick,
  onLogout,
}: {
  user: { nombre: string; email: string } | null;
  onMenuClick: () => void;
  onLogout: () => void;
}) {
  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-background px-4 lg:px-6">
      {/* Menu hamburguesa */}
      <button
        onClick={onMenuClick}
        className="rounded-lg p-2 hover:bg-accent lg:hidden"
      >
        <Menu className="h-5 w-5 text-muted-foreground" />
      </button>

      {/* Titulo */}
      <div className="hidden lg:block">
        <h1 className="text-lg font-semibold text-foreground">
          Portal de Soporte
        </h1>
      </div>

      {/* Usuario */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-3">
          <Avatar
            fallback={user?.nombre || 'Usuario'}
            size="sm"
          />
          <div className="hidden md:block">
            <p className="text-sm font-medium text-foreground">
              {user?.nombre || 'Usuario'}
            </p>
            <p className="text-xs text-muted-foreground">
              {user?.email || ''}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onLogout}
          className="text-muted-foreground hover:text-foreground"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    </header>
  );
}

export default function PortalLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { data: config } = usePortalConfig();
  const { showWarning, countdownSeconds, dismissWarning } = useSessionManager();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const enabledModules = config?.modulos_habilitados || {
    tickets: true,
    conversaciones: true,
    conocimiento: true,
  };

  return (
    <div className="min-h-screen bg-background">
      <PortalSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        enabledModules={enabledModules}
      />

      {/* Contenido principal */}
      <div className="lg:ml-64 min-h-screen">
        <PortalHeader
          user={user}
          onMenuClick={() => setSidebarOpen(true)}
          onLogout={handleLogout}
        />

        {/* Contenido de la pagina */}
        <main className="p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      {/* Session idle warning */}
      <SessionWarningDialog
        open={showWarning}
        countdownSeconds={countdownSeconds}
        onContinue={dismissWarning}
      />
    </div>
  );
}
